import React, { HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// Omit conflicting props from HTMLAttributes
type SafeHTMLAttributes = Omit<
  HTMLAttributes<HTMLDivElement>,
  'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag'
>;

export interface CardProps extends SafeHTMLAttributes {
  elevation?: 'sm' | 'md' | 'lg' | 'none';
  variant?: 'filled' | 'outlined' | 'glass';
  gradient?: boolean;
  gradientDirection?: 'primary' | 'secondary' | 'accent' | 'warm' | 'cool';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  rounded?: 'md' | 'lg' | 'xl' | '2xl';
  hover?: boolean;
  interactive?: boolean;
}

export const Card: React.FC<CardProps> = ({
  children,
  elevation = 'md',
  variant = 'filled',
  gradient = false,
  gradientDirection = 'warm',
  padding = 'md',
  rounded = 'xl',
  hover = false,
  interactive = false,
  className,
  ...props
}) => {
  const baseStyles = clsx(
    'transition-all duration-300',
    {
      'cursor-pointer': interactive,
    }
  );

  const elevationStyles = {
    none: '',
    sm: 'shadow-soft',
    md: 'shadow-medium',
    lg: 'shadow-large',
  };

  const variantStyles = {
    filled: 'bg-white',
    outlined: 'bg-white border-2 border-neutral-200',
    glass: 'glass',
  };

  const gradientStyles = gradient
    ? {
        primary: 'bg-gradient-cool',
        secondary: 'bg-gradient-to-br from-secondary-50 to-white',
        accent: 'bg-gradient-to-br from-accent-50 to-white',
        warm: 'bg-gradient-warm',
        cool: 'bg-gradient-cool',
      }[gradientDirection]
    : '';

  const paddingStyles = {
    none: 'p-0',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const roundedStyles = {
    md: 'rounded-lg',
    lg: 'rounded-xl',
    xl: 'rounded-2xl',
    '2xl': 'rounded-3xl',
  };

  const hoverStyles = hover || interactive ? 'hover:shadow-elevated' : '';

  const combinedStyles = clsx(
    baseStyles,
    elevationStyles[elevation],
    variantStyles[variant],
    gradientStyles,
    paddingStyles[padding],
    roundedStyles[rounded],
    hoverStyles,
    className
  );

  const motionProps = interactive
    ? {
        whileHover: { y: -4, scale: 1.01 },
        whileTap: { scale: 0.99 },
        transition: { duration: 0.2 },
      }
    : {};

  if (interactive) {
    return (
      <motion.div className={combinedStyles} {...motionProps} {...(props as any)}>
        {children}
      </motion.div>
    );
  }

  return (
    <div className={combinedStyles} {...props}>
      {children}
    </div>
  );
};

// Card sub-components for better composition
export const CardHeader: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div className={clsx('mb-4', className)} {...props}>
      {children}
    </div>
  );
};

export const CardTitle: React.FC<HTMLAttributes<HTMLHeadingElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <h3 className={clsx('text-xl font-bold text-neutral-900', className)} {...props}>
      {children}
    </h3>
  );
};

export const CardDescription: React.FC<HTMLAttributes<HTMLParagraphElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <p className={clsx('text-sm text-neutral-600 mt-1', className)} {...props}>
      {children}
    </p>
  );
};

export const CardBody: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div className={clsx(className)} {...props}>
      {children}
    </div>
  );
};

export const CardFooter: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div className={clsx('mt-4 pt-4 border-t border-neutral-200', className)} {...props}>
      {children}
    </div>
  );
};

export default Card;
